// Local storage for kiosks and staff phones (plan §4 D6): a read-only roster mirror, a signed
// outbox, and the device record. Rejected: PowerSync, ElectricSQL, Dexie Cloud, Zero; an
// append-only outbox plus a small read-only mirror needs no sync engine.
import { Dexie, type EntityTable } from "dexie";
import type { PreImage } from "@vavr/core";

export interface DeviceRecord {
  key: "device";
  device_id: string;
  site_id: string | null;
  kind: "kiosk" | "phone";
  status: "pending" | "active" | "revoked";
  fingerprint: string;
  keys: CryptoKeyPair; // private key non-extractable; IndexedDB stores CryptoKey by structured clone
  mirrorKey: CryptoKey; // AES-GCM, non-extractable: encrypts the roster mirror at rest
  api_base: string;
  enrolled_at: number;
}

export interface RosterRecord { key: "roster"; version: number; etag: string | null; iv: Uint8Array; ct: ArrayBuffer; fetched_at: number }

export interface OutboxBlob { sha256: string; type: string; bytes: Uint8Array; uploaded: boolean }

export interface OutboxEntry {
  id: string; // = pre.id
  created_at: number; // device clock
  pre: PreImage;
  device_sig: string;
  blobs: OutboxBlob[];
  state: "pending" | "sent" | "rejected";
  attempts: number;
  last_error: string | null;
  seq: number | null;
  event_hash: string | null;
  server_flags: string[];
}

export interface MetaRecord { key: string; value: unknown }
/** One entry of a door's Wi-Fi log (radio turns, links, relays, diagnostics), until uploaded. */
/** A record another device signed, heard on the emergency mesh; `uploaded` 1 once the cloud has it. */
export interface MeshRecord { id: string; pre: PreImage; device_sig: string; at: number; uploaded: 0 | 1 }
export interface LanLogEntry { id: string; at: number; kind: "radio" | "link" | "relay" | "diag"; data: Record<string, unknown> }

export class VavrDb extends Dexie {
  device!: EntityTable<DeviceRecord, "key">;
  roster!: EntityTable<RosterRecord, "key">;
  outbox!: EntityTable<OutboxEntry, "id">;
  meta!: EntityTable<MetaRecord, "key">;
  lanlog!: EntityTable<LanLogEntry, "id">;
  meshrec!: EntityTable<MeshRecord, "id">;
  /**
   * `legacy`: the name this database had before the product was renamed (pickmeup -> vavr). On the
   * first open, a phone or tablet that still has the old database hands its contents over (the
   * device key, the roster and unsent records) before anything else reads it, and the old one is
   * deleted, so nobody re-enrols or loses a record.
   */
  constructor(name = "vavr", legacy: string | null = name === "vavr" ? "pickmeup" : null) {
    super(name);
    this.version(1).stores({ device: "key", roster: "key", outbox: "id, state, created_at", meta: "key" });
    // 2: a door's Wi-Fi log (offline phases 2-3), kept until the cloud has it (LanLog).
    this.version(2).stores({ lanlog: "id, at" });
    // 3: records heard on the emergency mesh (EmergencyMesh), kept until the cloud has them.
    this.version(3).stores({ meshrec: "id, uploaded, at" });
    if (legacy) this.on("ready", (vip) => adoptLegacy(vip as VavrDb, legacy));
  }
}

const TABLES = ["device", "roster", "outbox", "meta"] as const;

async function adoptLegacy(db: VavrDb, legacy: string) {
  if (!(await Dexie.exists(legacy))) return;
  const old = new VavrDb(legacy, null);
  try {
    // Only into an empty database: if this one already has a device, it is the newer truth.
    if (!(await db.device.count())) {
      for (const t of TABLES) {
        const rows = await old.table(t).toArray();
        if (rows.length) await db.table(t).bulkPut(rows);
      }
    }
  } finally {
    old.close();
  }
  await Dexie.delete(legacy);
}
